import {useMutation} from '@tanstack/react-query';
import {config} from '../config';
import defaultPdfDataUrl from 'assets/documents/creditagreement_updated.pdf?inline';

export type SigningDemoType =
  | 'SIMPLE_SIGNING'
  | 'NORMAL_SIGNING'
  | 'IMMEDIATE_SIGNING';

// Not part of config: the demo always calls the real endpoint.
const IMMEDIATE_SIGNING_PATH = '/mock/demo/immediate/signing';

// DIDS requires a callback on immediate signing, but the demo doesn't use it.
// DIDS POSTs the (Base64) placeholder payload to this dummy endpoint (empty 200) when the
// signing completes.
const IMMEDIATE_SIGNING_CALLBACK_PATH = '/mock/demo/immediate/callback';
const IMMEDIATE_SIGNING_PLACEHOLDER_CALLBACK_PAYLOAD = 'ZGVtbw=='; // "demo"

export interface SigningDemoFormParams {
  governmentId: string;
  fullName: string;
  displayText: string;
  costBearer: string;
  legalGovernmentId: string;
  endUserIp: string;
  signingType: SigningDemoType;
  documentViewAuthRequired: boolean;
  // NORMAL_SIGNING only — ignored for SIMPLE_SIGNING.
  intentDisplayText?: string;
  // Base64 (no data: prefix) of the main PDF. Defaults to the bundled credit agreement.
  mainDocumentPdf?: PdfFile;
  mainDocumentAgreementType?: string;
  additionalDocuments?: AdditionalDocumentParams[];
  // NORMAL_SIGNING only. SIMPLE_SIGNING always uses governmentId/fullName above.
  signers?: SignerParams[];
}

export interface SignerParams {
  id: string;
  governmentId: string;
  fullName: string;
  firstName: string;
  lastName: string;
}

export type DocumentAction = 'VIEW' | 'SIGN';

export const AGREEMENT_TYPES = [
  'ECOMMERCE',
  'BILL PAYMENT',
  'BIOMETRIC AUTHENTICATION',
  'CHANGE PRE-REGISTERED ACCOUNT',
  'CLOSE SAVINGS ACCOUNT',
  'CONSUMER LOAN',
  'CONSUMER LOAN POWER OF ATTORNEY CONSOLIDATION',
  'CREDIT CARD AGREEMENT',
  'DIRECTDEBIT INFO',
  'DIRECT DEBIT REGISTRATION',
  'INSURANCEAGREEMENT',
  'MOTHERSHIP AGREEMENT',
  'PAYMENT CONSENT RB-API',
  'PAYMENT RB-API',
  'SAVINGS ACCOUNT APPLICATION',
  'UNSECURED LOAN AGREEMENT',
  'WITHDRAWAL RB-API',
] as const;

// The main document must have an agreementType; additional documents without
// one inherit the main document's in DIDS.
export const DEFAULT_MAIN_AGREEMENT_TYPE = 'ECOMMERCE';

export interface AdditionalDocumentParams {
  id: string;
  kind: 'PDF' | 'TEXT';
  // File name for uploaded documents, empty for typed text.
  fileName?: string;
  // Base64 (no data: prefix) for PDF, plain text for TEXT.
  content: string;
  // Sent as agreementType. Omitted when empty.
  agreementType: string;
  documentAction: DocumentAction;
}

export interface PdfFile {
  fileName: string;
  base64: string;
}

const stripDataUrlPrefix = (dataUrl: string) =>
  dataUrl.slice(dataUrl.indexOf(',') + 1);

export const DEFAULT_MAIN_PDF: PdfFile = {
  fileName: 'creditagreement_updated.pdf',
  base64: stripDataUrlPrefix(defaultPdfDataUrl),
};

export interface CreateSigningDemoResponse {
  signingSessionId: string;
  providerId: string;
  signerResponse: Array<{
    signerId: string;
    signingUrl: string;
    governmentId?: string;
    fullName?: string;
    providerId?: string;
  }>;
  autoStartToken?: string;
  provider: string;
}

export const useSigningDemo = () => {
  return useMutation({
    mutationKey: ['demo', 'signing', 'create'],
    mutationFn: async (params: SigningDemoFormParams) => {
      const isImmediate = params.signingType === 'IMMEDIATE_SIGNING';
      // NORMAL_SIGNING and IMMEDIATE_SIGNING share the document/signer form.
      const documentBased = params.signingType !== 'SIMPLE_SIGNING';
      const signerParams: SignerParams[] =
        documentBased && params.signers?.length
          ? params.signers.slice(0, isImmediate ? 1 : undefined)
          : [
              {
                id: 'signer',
                governmentId: params.governmentId,
                fullName: params.fullName,
                firstName: '',
                lastName: '',
              },
            ];

      // Names left blank are derived from the full name.
      const signers = signerParams.map((signer) => {
        const [firstPart, ...lastParts] = signer.fullName.trim().split(' ');
        return {
          firstName: signer.firstName || firstPart,
          lastName: signer.lastName || lastParts.join(' ') || firstPart,
          fullName: signer.fullName,
          ...(signer.governmentId && {
            naturalGovernmentId: signer.governmentId,
          }),
          // DIDS derives the document-view auth gate from alreadyLoggedIn (true = signer already
          // authenticated elsewhere, e.g. Direct BankID, so the gate is skipped) - see EID-609.
          // Not part of the immediate signer model (DIDS always sets it to true).
          ...(!isImmediate && {
            alreadyLoggedIn: !params.documentViewAuthRequired,
          }),
          clientRedirectURLs: {
            onTaskCompletedUrl: `${config.clientHost}/demo-signing?status=completed`,
            onTaskCanceledUrl: `${config.clientHost}/demo-signing?status=canceled`,
            onTaskPostponedUrl: `${config.clientHost}/demo-signing?status=postponed`,
          },
        };
      });

      const mainDocument = documentBased
        ? {
            contentType: 'BASE64_ENCODED_PDF',
            agreementValue: (params.mainDocumentPdf ?? DEFAULT_MAIN_PDF).base64,
            agreementType:
              params.mainDocumentAgreementType || DEFAULT_MAIN_AGREEMENT_TYPE,
          }
        : {
            contentType: 'TEXT',
            agreementValue: params.displayText,
            agreementType: 'Ecommerce',
          };

      // Empty text documents are skipped (agreementValue must not be blank).
      const additionalDocuments = (params.additionalDocuments ?? [])
        .filter((doc) => doc.content.trim() !== '')
        .map((doc) => ({
          contentType: doc.kind === 'PDF' ? 'BASE64_ENCODED_PDF' : 'TEXT',
          agreementValue: doc.content,
          ...(doc.agreementType && {agreementType: doc.agreementType}),
          documentAction: doc.documentAction,
        }));

      const common = {
        mainDocument,
        ...(documentBased &&
          params.intentDisplayText && {
            intent: {displayText: params.intentDisplayText},
          }),
        ...(documentBased &&
          additionalDocuments.length > 0 && {additionalDocuments}),
        signers,
        locale: 'sv_SE',
        costBearer: params.costBearer,
        ...(params.legalGovernmentId.trim() && {
          legalGovernmentId: params.legalGovernmentId.trim(),
        }),
      };

      const body = isImmediate
        ? {
            ...common,
            providerConfiguration: {endUserIp: params.endUserIp},
            callback: {
              uri: `${config.bankIdHost}${IMMEDIATE_SIGNING_CALLBACK_PATH}`,
              payload: IMMEDIATE_SIGNING_PLACEHOLDER_CALLBACK_PAYLOAD,
            },
          }
        : {
            ...common,
            signingType: params.signingType,
            userClientType: 'WEB',
            providerConfig: {endUserIp: params.endUserIp},
          };

      // Dev-only convenience: forces DIDS to create this NORMAL_SIGNING session with the SE_MBID
      // provider regardless of country/config, so the demo can exercise the document-review flow
      // (real routing only ever picks SE_MBID for SIMPLE_SIGNING). Ignored by DIDS in prod. Lives
      // on POST /signing itself now, not on the bounce redirect — see EID-567.
      const useProviderOverride =
        (params.signingType === 'NORMAL_SIGNING' || isImmediate) &&
        config.env !== 'production';
      const path = isImmediate
        ? IMMEDIATE_SIGNING_PATH
        : config.signingPathPrefix;
      const url = `${config.bankIdHost}${path}${
        useProviderOverride ? '?provider_override=SE_MBID' : ''
      }`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          apikey: config.apiKey,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => null);
        throw new Error(
          error?.message ?? `${response.status} ${response.statusText}`,
        );
      }

      return (await response.json()) as CreateSigningDemoResponse;
    },
  });
};
